/** Tiny DOM helpers. No framework: elements are built and handed back. */

export type Child = Node | string | number | null | undefined | false;

export interface ElProps {
  class?: string;
  text?: string;
  html?: never;
  /** Anything else lands as an attribute (or a property for `value`/`checked`). */
  [key: string]: unknown;
}

const PROPERTY_KEYS = new Set(['value', 'checked', 'disabled', 'selected', 'textContent']);

/**
 * A finger rather than a mouse, as far as the browser will say.
 *
 * Copy that names a gesture has to name the one the reader has: "scroll to
 * zoom" is an instruction for a device that is not in their hand. Defended
 * against environments without `matchMedia` (jsdom, and very old browsers),
 * where a mouse is the safer guess.
 */
export function coarsePointer(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  try {
    return window.matchMedia('(pointer: coarse)').matches;
  } catch {
    return false;
  }
}

export function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  props: ElProps = {},
  ...children: Child[]
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  for (const [key, value] of Object.entries(props)) {
    if (value === undefined || value === null || value === false) continue;
    if (key === 'text') {
      node.textContent = String(value);
    } else if (key === 'style') {
      applyInlineStyle(node, String(value));
    } else if (key.startsWith('on') && typeof value === 'function') {
      node.addEventListener(key.slice(2).toLowerCase(), value as EventListener);
    } else if (PROPERTY_KEYS.has(key)) {
      (node as unknown as Record<string, unknown>)[key] = value;
    } else if (value === true) {
      node.setAttribute(key, '');
    } else {
      node.setAttribute(key, String(value));
    }
  }
  append(node, children);
  return node;
}

/**
 * `style: 'gap:8px'` applied one declaration at a time through CSSOM.
 *
 * Not `setAttribute('style', …)`: the deployed CSP is `style-src 'self'` with
 * no `'unsafe-inline'`, which blocks a `style` attribute. CSSOM writes are
 * outside CSP's reach, so the same declarations land without loosening it.
 * Declarations are simple `property: value` pairs — no `url(…)`, no `data:`.
 */
export function applyInlineStyle(node: HTMLElement, css: string): void {
  for (const declaration of css.split(';')) {
    const colon = declaration.indexOf(':');
    if (colon < 0) continue;
    const property = declaration.slice(0, colon).trim();
    const value = declaration.slice(colon + 1).trim();
    if (property && value) node.style.setProperty(property, value);
  }
}

export function append(parent: Node, children: Child[]): void {
  for (const child of children) {
    if (child === null || child === undefined || child === false) continue;
    parent.appendChild(typeof child === 'object' ? child : document.createTextNode(String(child)));
  }
}

/** Replace a node's children, dropping the nulls a conditional list leaves behind. */
export function setChildren(parent: Node, children: Child[]): void {
  clear(parent);
  append(parent, children);
}

export function clear(node: Node): void {
  while (node.firstChild) node.removeChild(node.firstChild);
}

export function button(
  label: string,
  onClick: (event: MouseEvent) => void,
  props: ElProps = {},
): HTMLButtonElement {
  const cls = props.class ? `btn ${props.class}` : 'btn';
  return el('button', { ...props, class: cls, type: 'button', text: label, onClick });
}

/** A labelled input wrapped in the shared `.field` shell. */
export interface FieldOptions extends ElProps {
  label: string;
  hint?: string;
  name: string;
}

export interface Field<T extends HTMLElement = HTMLInputElement> {
  root: HTMLElement;
  input: T;
  setError(message: string | null): void;
}

function fieldShell<T extends HTMLElement>(
  label: string,
  name: string,
  input: T,
  hint?: string,
): Field<T> {
  const id = `f-${name}-${Math.random().toString(36).slice(2, 8)}`;
  input.id = id;
  const error = el('div', { class: 'field__error', role: 'alert' });
  error.hidden = true;
  const root = el(
    'div',
    { class: 'field' },
    el('label', { for: id, text: label }),
    input,
    hint ? el('div', { class: 'field__hint', text: hint }) : null,
    error,
  );
  return {
    root,
    input,
    setError(message) {
      error.textContent = message ?? '';
      error.hidden = !message;
      root.classList.toggle('field--error', Boolean(message));
      if (message) input.setAttribute('aria-invalid', 'true');
      else input.removeAttribute('aria-invalid');
    },
  };
}

export function textField(options: FieldOptions): Field<HTMLInputElement> {
  const { label, hint, name, ...rest } = options;
  const input = el('input', { type: 'text', name, ...rest });
  return fieldShell(label, name, input, hint);
}

export function selectField(
  options: FieldOptions & { options: { value: string; label: string }[] },
): Field<HTMLSelectElement> {
  const { label, hint, name, options: items, ...rest } = options;
  const select = el(
    'select',
    { name, ...rest },
    ...items.map((o) => el('option', { value: o.value, text: o.label })),
  );
  if (typeof rest.value === 'string') select.value = rest.value;
  return fieldShell(label, name, select, hint);
}

/**
 * A count on a button — submissions waiting, say. Nothing at all for zero, so
 * a button with no news looks exactly as it did; the label is what a screen
 * reader hears in place of the bare number.
 */
export function countBadge(count: number, label: string): HTMLElement | null {
  if (count <= 0) return null;
  return el('span', {
    class: 'count-badge',
    'data-role': 'count',
    role: 'img',
    'aria-label': label,
    text: count > 99 ? '99+' : String(count),
  });
}

/** A read-only field with a copy button. Copy falls back to select-all. */
export function copyRow(value: string, label = 'Copy'): HTMLElement {
  const input = el('input', { readonly: true, value, 'aria-label': 'Link', spellcheck: 'false' });
  input.addEventListener('focus', () => input.select());
  const btn = button(label, async () => {
    try {
      await navigator.clipboard.writeText(input.value);
      btn.textContent = 'Copied';
      setTimeout(() => (btn.textContent = label), 1400);
    } catch {
      input.select();
      btn.textContent = 'Press Ctrl+C';
      setTimeout(() => (btn.textContent = label), 2200);
    }
  }, { class: 'btn--small' });
  return el('div', { class: 'copy' }, input, btn);
}

/** Swaps a button into a busy state for the duration of an async action. */
export async function withBusy<T>(btn: HTMLButtonElement, run: () => Promise<T>): Promise<T> {
  const label = btn.textContent;
  btn.setAttribute('aria-busy', 'true');
  btn.disabled = true;
  try {
    return await run();
  } finally {
    btn.removeAttribute('aria-busy');
    btn.disabled = false;
    btn.textContent = label;
  }
}
